<template>
  <content-loader v-if="showContentLoader" />
  <div
    ref="contentRef"
    class="page"
  >
    <riseup-header
      :flow-config-id="FlowConfigIdType.Prograd"
      :force-background-color="'#E1F1F2'"
    />
    <div ref="iframeContainerRef">
      <iframe
        v-if="htmlContent"
        ref="iframeRef"
        :srcdoc="htmlContent"
        style="width: 100%; border: none;"
        class="html-container"
        sandbox="allow-same-origin allow-popups"
        @load="setupIframeLinkHandler"
      />
    </div>
    <div class="prograd-bottom-section-container">
      <h1>Trending Now 🔥</h1>
      <div class="task-container">
        <div
          v-for="(task, index) in trendingTasks"
          :key="index"
          class="task-card"
          @click="handleTaskClick(task)"
        >
          <img
            :src="task.imgSrc"
            class="task-image"
          >
          <div class="task-details">
            <div class="reward-row">
              <div class="green-box">
                <img
                  src="../../../assets/prograd/icons/green-circle.svg"
                  class="icon"
                >
                <span>£{{ task.cash }}</span>
              </div>
              <div
                v-if="task.diamonds"
                class="diamond-box"
              >
                <img
                  src="../../../assets/prograd/icons/pink-diamond.svg"
                  class="icon"
                >
                <span>{{ task.diamonds }}</span>
              </div>
              <button class="green-button">
                <span>Earn</span>
              </button>
            </div>
            <div class="task-info">
              <p>{{ task.text }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="divider-line" />
    </div>
    <div class="prograd-bottom-section-container">
      <h1>Quick surveys to complete 🕙</h1>
      <div class="task-small-container">
        <div
          v-for="task in surveyTasks"
          :key="task.name"
          class="task-small"
          @click="handleTaskClick(task)"
        >
          <img
            class="task-image"
            :src="task.imgSrc"
          >
          <div class="task-details">
            <div class="reward-row space-between">
              <div class="green-box no-border white">
                <img
                  src="../../../assets/prograd/icons/green-circle.svg"
                  class="icon"
                >
                <span>£{{ task.cash }}</span>
              </div>
              <div class="diamond-box">
                <img
                  src="../../../assets/prograd/icons/pink-diamond.svg"
                  class="icon"
                >
                <span>{{ task.diamonds }}</span>
              </div>
            </div>
            <div class="task-info green-text">
              <p>Surveys</p>
            </div>
          </div>
        </div>
      </div>
      <div class="divider-line" />
    </div>

    <div class="prograd-bottom-section-container">
      <h1>High rewards 💰</h1>
      <div class="task-container">
        <div
          v-for="(task, index) in highRewardTasks"
          :key="index"
          class="task-card"
          @click="handleTaskClick(task, true)"
        >
          <img
            :src="task.imgSrc"
            class="task-image"
          >
          <div class="task-details">
            <div class="reward-row space-between">
              <div class="green-box no-border white">
                <img
                  src="../../../assets/prograd/icons/green-circle.svg"
                  class="icon"
                >
                <span>£{{ task.cash }}</span>
              </div>
              <div
                v-if="task.diamonds"
                class="diamond-box"
              >
                <img
                  src="../../../assets/prograd/icons/pink-diamond.svg"
                  class="icon"
                >
                <span>{{ task.diamonds }}</span>
              </div>
            </div>
            <div class="task-info green-text">
              <p>Games</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Segment from '../../../Segment';
import useContentLoader from '../../../composables/useContentLoader';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { FlowConfigIdType } from '../../../types/otsConfig';
import ContentLoader from '../../Common/ContentLoader.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import config from '../../../config';

import animalsImg from '../../../assets/prograd/tasks/animals-wide.png';
import surveysImg from '../../../assets/prograd/tasks/branded-small.svg';
import cashbackImg from '../../../assets/prograd/tasks/cashback-small.svg';
import custardImg from '../../../assets/prograd/tasks/custard-wide.svg';
import dscoutImg from '../../../assets/prograd/tasks/dscout-small.svg';
import fiendsImg from '../../../assets/prograd/tasks/fiends-wide.png';
import fortuneImg from '../../../assets/prograd/tasks/fortune-wide.png';
import freecashImg from '../../../assets/prograd/tasks/freecash-wide.svg';
import ipsosImg from '../../../assets/prograd/tasks/ipsos-wide.svg';
import monopolyImg from '../../../assets/prograd/tasks/monopoly-wide.png';
import swagbucksImg from '../../../assets/prograd/tasks/swagbucks-small.svg';
import voiceImg from '../../../assets/prograd/tasks/voice-wide.svg';

import { useSessionStore } from '../../../stores/session';

const { insights, populateInsights } = useInsights();
const contentRef = ref(null);
const route = useRoute();
const iframeRef = ref(null);
const iframeContainerRef = ref(null);
const { getOtsJobConfig } = useOtsJob();
const sessionStore = useSessionStore();

const otsJobConfig = await getOtsJobConfig();

const {
  showContentLoader,
} = useContentLoader();

const trendingTasks = ref([
  {
    name: 'ipsos',
    imgSrc: ipsosImg,
    diamonds: 2,
    cash: '4.50',
    text: 'The easiest site to earn with surveys and see if they are worth your time 🔥',
  },
  {
    name: 'freecash',
    imgSrc: freecashImg,
    diamonds: 4,
    cash: '4.50',
    text: 'Freecash: earn £30-£100 monthly by testing games and apps -> $5 welcome bonus',
  },
  {
    name: 'custard',
    imgSrc: custardImg,
    diamonds: 2,
    cash: '4.50',
    text: 'Get a £2 Joining Bonus + Earn Money by Completing Surveys, Playing Games & Much More! 🌟✨',
  },
  {
    name: 'voice',
    imgSrc: voiceImg,
    diamonds: 2,
    cash: '4.50',
    text: 'Love binging Netflix and TV? Spill your thoughts & get paid! (UK, 16+ Only) 🍿💰',
  },
]);

const surveyTasks = ref([
  {
    diamonds: 2,
    cash: '4.50',
    name: 'surveys',
    imgSrc: surveysImg,
  },
  {
    diamonds: 1,
    cash: '4.30',
    name: 'dscout',
    imgSrc: dscoutImg,
  },
  {
    diamonds: 2,
    cash: '4.50',
    name: 'swagbucks',
    imgSrc: swagbucksImg,
  },
  {
    diamonds: 4,
    cash: 145.77,
    name: 'cashback',
    imgSrc: cashbackImg,
  },
]);

const highRewardTasks = ref([
  {
    diamonds: 9,
    cash: '161.10',
    name: 'animals',
    imgSrc: animalsImg,
  },
  {
    diamonds: null,
    cash: '145.77',
    name: 'monopoly',
    imgSrc: monopolyImg,
  },
  {
    diamonds: 1,
    cash: '110.30',
    name: 'fiends',
    imgSrc: fiendsImg,
  },
  {
    diamonds: 11,
    cash: '110.75',
    name: 'fortune',
    imgSrc: fortuneImg,
  },
]);
const handleTaskClick = (task, appendPlatform = false) => {
  const { customerId } = sessionStore;
  const platform = window.navigator.userAgent.includes('iPhone') ? 'ios' : 'and';
  window.open(`${config.get().ruthUrl}/ots?customer_id=${customerId}&to=${task.name}${appendPlatform ? `_${platform}` : ''}`, '_blank');
};

const htmlContent = ref('');
onMounted(async () => {
  const queryParam = route.query || null;

  const { customerId } = sessionStore;

  if (!insights.value || insights.value.length === 0) {
    await populateInsights(otsJobConfig.jobId);
  }
  htmlContent.value = `
  <!DOCTYPE html>
  <html>
    <head>
      <style>
        body {
          margin: 0;
        }
        body > :first-child {
          padding-bottom: 80px !important; /* Adjust as needed */
          font-family: 'CircularStd', sans-serif !important;
        }
      </style>
    </head>
    <body>
      ${insights.value[0]?.html}
    </body>
  </html>
`;

  Segment.trackUserGot('ProgradOtsCustomSuccessPageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig });

  adjustIframeHeight(); // Initial adjustment
  window.addEventListener('resize', adjustIframeHeight);
});

const adjustIframeHeight = () => {
  if (iframeRef.value && iframeRef.value.contentWindow) {
    setTimeout(() => {
      const iframeDocument = iframeRef.value.contentWindow.document;
      const iframeHeight = iframeDocument.documentElement.scrollHeight || iframeDocument.body.scrollHeight;
      console.log('iframeHeight', iframeHeight);

      iframeContainerRef.value.style.height = `${iframeHeight}px`;
      iframeRef.value.style.height = `${iframeHeight}px`;
    }, 100); // Adjust delay as needed
  }
};

const setupIframeLinkHandler = () => {
  if (iframeRef.value && iframeRef.value.contentWindow) {
    const iframeDocument = iframeRef.value.contentWindow.document;
    const links = iframeDocument.getElementsByTagName('a');

    Array.from(links).forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        window.open(link.href, '_blank');
      });
    });
  }

  adjustIframeHeight();
};

</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

/* Layout & Page Structure */
.page {
  background-color: #E1F1F2;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.content {
  margin-top: 30px;
  padding-left: 25px;
  padding-right: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.html-container {
  width: 100%;
}

.divider-line {
  height: 1px;
  background-color: #205441;
  opacity: 0.2;
  width: 100%;
}

.prograd-bottom-section-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  margin: 0 auto;
  margin-bottom: 50px;
  font-family: CircularStd;
}

.green-bottom {
  margin: 0 auto;
  width: 90%;
  background-color: #205441;
  border-radius: 40px;
  justify-content: flex-start;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 25px;
  margin-bottom: 20px;
}

/* Headings & Text */
h1 {
  font-family: CircularStd;
  font-size: 28px;
  font-weight: 900;
  color: black;
  align-self: flex-start;
  margin-bottom: 20px;
  width: 90%;
}

.task-info p {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #333;
  margin: 0;
}

.green-text p {
  color: #269B74;
}

.green-bottom p {
  color: white;
  font-size: 16px;
  font-family: CircularStd;
  line-height: 28px;
  word-wrap: break-word;
  width: 100%;
  margin-top: 36px;
}

/* Task Containers */
.task-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
  cursor: pointer;
}

.task-small-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  gap: 25px;
  margin-bottom: 20px;
}

/* Task Cards */
.task-card {
  background-color: white;
  border-radius: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.task-small {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .task-details {
    padding: 10px;
  }
}

.task-details {
  padding: 16px 20px 24px;
}

.task-image {
  width: 100%;
  object-fit: contain;
}

.task-wide {
  width: 105%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

/* Reward Elements */
.reward-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  &.space-between {
    justify-content: space-between;
    margin-bottom: 5px;

    .green-box {
      padding: 0px;
    }
  }
}

.green-box, .diamond-box {
  display: flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  gap: 6px;
  height: 35px;
}

.green-box {
  border: 1px solid #e0e0e0;
  color: black;
  background-color: #e9fffd;
  font-family: CircularStd;
  font-weight: 500;
  font-size: 16px;

  &.no-border {
    border: 0px;
  }

  &.white {
    background-color: white;
  }
}

.diamond-box {
  border: 1px solid rgba(168, 45, 243, 0.1);
  font-weight: 500;
  font-size: 16px;
}

.icon {
  width: 20px;
  height: 20px;
}

/* Buttons */
.green-button {
  background-color: #269B74;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-left: auto;
  height: 35px;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.green-bottom .yellow-button {
  background-color: #FFCF03;
  border-radius: 65px;
  color: #205441;
  border: none;
  margin-top: 34px;
  cursor: pointer;
  font-size: 15px;
  font-family: CircularStdBook;
  font-weight: 900;
  height: 52px;
  margin-bottom: 10px;
  box-shadow: 0px 12px 24px rgba(32, 33, 37, 0.12);
  width: 100%;
}

.green-bottom img {
  width: 100%;
}
</style>
